export const EMAIL_UTM_PREFIX = 'email-';

/**
 * Canonical value stored in DynamoDB for "no utm_source param in URL".
 * HTPageviewAggregations always stores the string "none" (see GA4Ingest/index.ts
 * UTM_NONE constant). HTUserFunnel stores null/undefined for signups without
 * a utm. We normalize both to this constant when grouping + filtering.
 */
export const NONE_UTM = 'none';

/**
 * Prefixes that identify a utm_source as a campaign identifier rather than a
 * page slug. Campaign utms (email sends, social posts, paid ad clicks) travel
 * ON the URL — they're observed as stored `UtmSource` values on the rows the
 * visitor actually hits.
 */
export const CAMPAIGN_UTM_PREFIXES = ['email-', 'li-', 'tw-', 'fb-', 'ig-'];

/**
 * Canonical utm value for the marketing homepage (`/`).
 */
export const HOMEPAGE_UTM = 'homepage';

/**
 * Prefix for blog-post utm sources (`blog-<post-slug>`).
 */
export const BLOG_POST_UTM_PREFIX = 'blog-';

export type UtmSourceCategory = 'campaign' | 'page';

/**
 * Classifies a utm source string by how the funnel resolver matches it.
 *
 * - `page`: matched by pagePath. Only explicit known page-slugs —
 *   HOMEPAGE_UTM, and any utm starting with BLOG_POST_UTM_PREFIX.
 *   If you add a new page-slug utm (e.g. 'pricing'), list it here explicitly.
 *
 * - `campaign`: matched by stored UtmSource value. Default for everything
 *   else — `none`, all CAMPAIGN_UTM_PREFIXES (email-, li-, fb-, tw-, ig-),
 *   and any arbitrary campaign identifier (iphone-smoketest, launch-week, etc.)
 *   which partitions pageviews cleanly by their stored utm value.
 */
export const classifyUtmSource = (source: string): UtmSourceCategory => {
  if (source === HOMEPAGE_UTM) {
    return 'page';
  }
  if (source.startsWith(BLOG_POST_UTM_PREFIX)) {
    return 'page';
  }
  return 'campaign';
};

export const FUNNEL_STAGE_CATEGORY = {
  HUBSPOT: 'hubspot',
  COHORT: 'cohort',
} as const;

export type FunnelStageCategory =
  (typeof FUNNEL_STAGE_CATEGORY)[keyof typeof FUNNEL_STAGE_CATEGORY];

export const FUNNEL_STAGE_NAME = {
  SENT: 'Sent (campaign total)',
  DELIVERED: 'Delivered (campaign total)',
  OPENS: 'Opens (campaign total)',
  CLICKS: 'Clicks (campaign total)',
  /**
   * Date-filtered unique visitor count for the campaign's utm_source, as
   * opposed to the lifetime HubSpot Clicks total above. Reflects how many
   * unique visitors (returning + new) were recorded within the selected date
   * range for this email's utm.
   */
  UNIQUE_VISITS_IN_RANGE: 'Visits in range',
  SIGNUP_PAGE_VIEWS: 'Signup page views',
  SIGNUPS: 'Signups',
  ACCOUNT_CONNECTED: 'Account connected',
  DOBOX_INTERACTION: 'DoBox interaction',
  RETAINED_1D: 'Retained 1d',
  RETAINED_7D: 'Retained 7d',
  RETAINED_30D: 'Retained 30d',
} as const;

export type FunnelStageName =
  (typeof FUNNEL_STAGE_NAME)[keyof typeof FUNNEL_STAGE_NAME];

export const EMAIL_STAGE_NAMES: ReadonlySet<string> = new Set<string>([
  FUNNEL_STAGE_NAME.SENT,
  FUNNEL_STAGE_NAME.DELIVERED,
  FUNNEL_STAGE_NAME.OPENS,
  FUNNEL_STAGE_NAME.CLICKS,
  FUNNEL_STAGE_NAME.UNIQUE_VISITS_IN_RANGE,
]);

/**
 * Earliest date for which UniqueVisitorCount data exists in DynamoDB.
 * Used by the UI to show a coverage notice for date ranges that predate
 * the backfill.
 */
export const FUNNEL_FIRST_VISIT_COVERAGE_START = '2026-04-06';

// ---------------------------------------------------------------------------
// Intent-segment classifier
// ---------------------------------------------------------------------------

export type IntentSegment =
  | 'product'
  | 'comparison'
  | 'informational'
  | 'editorial'
  | 'social'
  | 'email'
  | 'paid'
  | 'direct'
  | 'organic-search'
  | 'ai-referral'
  | 'referral'
  | 'other'
  | 'unknown-first-touch';

/**
 * The dashboard reports these shares, which are deliberately disjoint:
 *
 *   HIGH    — arrived already evaluating us or a competitor
 *   PAID    — we bought the click
 *   SOCIAL  — organic posts on LinkedIn, X, Instagram, Facebook
 *   EMAIL   — our own list
 *   DIRECT  — no referrer at all
 *   ORGANIC — search engines
 *   LOW     — arrived for the content, not for us
 *
 * Social and email are kept apart from PAID because "campaign" would be
 * ambiguous next to it — ads run in campaigns too — and apart from each other
 * because they are different levers with different work behind them.
 *
 * DIRECT and ORGANIC each get a row rather than a place on the high/low axis,
 * because neither sits on it. That axis asks what the visitor came for, and a
 * channel does not answer it: an organic visit is high or low intent depending
 * entirely on the query, which we cannot see.
 *
 * **Direct is not scored high despite looking like it should be.** Typing the
 * URL is high intent, and so is a link a colleague pasted in Slack, which lands
 * here too because those clients send no referrer. But `capture()` in the
 * marketing site's BaseLayout writes nothing without analytics consent, so a
 * first touch is recorded only when the visitor had already accepted the
 * banner. Anyone who finds us via search, ignores the banner, then returns by
 * typing the URL is stamped `direct` — their real first touch is lost. Consent
 * runs near 18% among signups, so that path is common and it inflates this
 * bucket. Scoring it high would let the segment flatter itself with signups we
 * simply failed to measure.
 *
 * AI referral IS scored high: being named by an assistant means somebody was
 * asking for tools like ours.
 *
 * Only `unknown-first-touch` sits outside all of them, because it is missing
 * data rather than a kind of visitor. So the shares fall short of 100 by
 * exactly the unattributed share, which is a number worth seeing.
 */
export const INTENT_HIGH_SEGMENTS: ReadonlySet<IntentSegment> =
  new Set<IntentSegment>(['product', 'comparison', 'ai-referral']);

export const INTENT_PAID_SEGMENTS: ReadonlySet<IntentSegment> =
  new Set<IntentSegment>(['paid']);

export const INTENT_SOCIAL_SEGMENTS: ReadonlySet<IntentSegment> =
  new Set<IntentSegment>(['social']);

export const INTENT_EMAIL_SEGMENTS: ReadonlySet<IntentSegment> =
  new Set<IntentSegment>(['email']);

export const INTENT_DIRECT_SEGMENTS: ReadonlySet<IntentSegment> =
  new Set<IntentSegment>(['direct']);

export const INTENT_ORGANIC_SEGMENTS: ReadonlySet<IntentSegment> =
  new Set<IntentSegment>(['organic-search']);

export const INTENT_LOW_SEGMENTS: ReadonlySet<IntentSegment> =
  new Set<IntentSegment>(['informational', 'editorial', 'referral', 'other']);

/** Forward-only: first-touch data does not exist before this deploy date. */
export const FIRST_TOUCH_COVERAGE_START = '2026-07-29';

export const ACQUISITION_SOURCE_QUESTION_START = '2026-10-19';

/** Manual per-key reclassification (checked first). Keyed by utm key. */
export const INTENT_SEGMENT_OVERRIDES: Record<string, IntentSegment> = {};

const PRODUCT_PAGE_KEYS = new Set<string>([
  'homepage',
  'workflows',
  'brain',
  'pricing',
  'platform',
  'mcp',
  'assistant',
  'how-it-works',
  'security',
  'teams',
  'ai-task-capture',
  'replaces',
  'calendar',
]);

const PRODUCT_PAGE_PREFIXES = [
  'dobox',
  'integrations/',
  'roles/',
  'industries/',
  'use-cases/',
  'compare/',
  'migrate/',
  'tools',
];

const COMPARISON_BLOG_MARKERS = ['-review', 'best-', '-alternatives', '-vs-'];
const INFORMATIONAL_BLOG_MARKERS = ['-statistics', '-stats'];

/** Locales the blog is translated into, as they appear in the URL. */
export const TRANSLATED_BLOG_LOCALES = ['pt-br'] as const;

/** Suffixes the in-post CTA buttons add to a blog tag (InlineCTA.astro). */
const BLOG_CTA_SUFFIXES = ['-mid-cta', '-end-cta'];

/**
 * Mirror of getUtmSource in marketing/src/utils/signup.ts. Keep in sync.
 *
 * `/`                     → `homepage`
 * `/blog/<slug>`          → `blog-<slug>`
 * `/pt-br/blog/<slug>`    → `blog-pt-br-<slug>`
 * else: path with leading/trailing slashes stripped
 */
export function pagePathToUtmKey(pagePath: string): string {
  if (pagePath === '/' || pagePath === '') return HOMEPAGE_UTM;
  for (const locale of TRANSLATED_BLOG_LOCALES) {
    const prefix = `/${locale}/blog/`;
    if (pagePath.startsWith(prefix)) {
      return `${BLOG_POST_UTM_PREFIX}${locale}-${pagePath.slice(prefix.length).replace(/\/$/, '')}`;
    }
  }
  if (pagePath.startsWith('/blog/')) {
    return `${BLOG_POST_UTM_PREFIX}${pagePath.slice('/blog/'.length).replace(/\/$/, '')}`;
  }
  return pagePath.replace(/^\/|\/$/g, '');
}

/**
 * The page a `blog-` tag names, for matching it against pageviews. Inverse of
 * pagePathToUtmKey for blog tags, and also strips the -mid-cta / -end-cta
 * suffix the in-post buttons add: `blog-<slug>-end-cta` was being matched
 * against `/blog/<slug>-end-cta`, a page that does not exist, so those
 * signups never met their post's visitors.
 *
 * `blog-<slug>[-mid-cta|-end-cta]`          → `/blog/<slug>`
 * `blog-pt-br-<slug>[-mid-cta|-end-cta]`    → `/pt-br/blog/<slug>`
 */
export function blogUtmKeyToPath(utmKey: string): string | null {
  if (!utmKey.startsWith(BLOG_POST_UTM_PREFIX)) return null;
  let slug = utmKey.slice(BLOG_POST_UTM_PREFIX.length);
  const suffix = BLOG_CTA_SUFFIXES.find((s) => slug.endsWith(s));
  if (suffix) slug = slug.slice(0, -suffix.length);
  for (const locale of TRANSLATED_BLOG_LOCALES) {
    if (slug.startsWith(`${locale}-`)) {
      return `/${locale}/blog/${slug.slice(locale.length + 1)}`;
    }
  }
  return `/blog/${slug}`;
}

/**
 * Paid-acquisition prefixes. Convention is `<network>-<country>[-<detail>]`,
 * e.g. `gads-us-alternatives`, `meta-br-smb`.
 *
 * These MUST stay distinct from CAMPAIGN_UTM_PREFIXES, where `fb-`/`ig-` mean
 * ORGANIC Meta posts. Reusing those for ads would merge paid and organic Meta
 * into one segment with no way to separate them afterward.
 */
// `chatgpt-ad` covers `chatgpt-ad1` and its successors, the OpenAI placement
// first run 2026-09-07. It is deliberately NOT written `oai-`: the two signups
// already in `HTUserFunnel` carry `chatgpt-ad1`, and a prefix that does not
// match them leaves the only paid signups we have filed as low intent.
export const PAID_UTM_PREFIXES = ['gads-', 'meta-', 'chatgpt-ad'];

/** Organic social posts. Subset of CAMPAIGN_UTM_PREFIXES, which stays as-is
 *  because it drives the older page-vs-campaign split in the source dropdown. */
export const SOCIAL_UTM_PREFIXES = ['li-', 'tw-', 'fb-', 'ig-'];

/** Our own list. */
export const EMAIL_UTM_PREFIXES = ['email-'];

/**
 * Channel values, written by `referrerSource()` in the marketing site's
 * BaseLayout when a visit carries no `utm_source`. They reach us only through
 * `FirstTouchUtmSource` — last touch stays "which of our pages sent the click"
 * and never holds one of these.
 *
 * The prefixes exist because that function already namespaces what it writes:
 * `organic-google`, `ai-chatgpt`, `referral-<host>`, and the bare `direct`.
 * Keep them in step with the SEARCH_HOSTS and AI_HOSTS tables over there.
 */
export const DIRECT_UTM_KEY = 'direct';
export const ORGANIC_UTM_PREFIXES = ['organic-'];
export const AI_UTM_PREFIXES = ['ai-'];
export const REFERRAL_UTM_PREFIXES = ['referral-'];

/**
 * Market (ISO country) an ad click came from, taken from the second token of a
 * paid utm key. Returns null for non-paid keys or paid keys with no market,
 * which is what makes US-vs-Brazil comparison possible — the cookie carries
 * only utm_source, so the market has to live in the string itself.
 */
export function parseAdMarket(key: string): string | null {
  if (!PAID_UTM_PREFIXES.some((p) => key.startsWith(p))) return null;
  const market = key.split('-')[1];
  return market && /^[a-z]{2}$/.test(market) ? market : null;
}

/**
 * Takes either a page key (`homepage`, `blog-<slug>`) or a channel value
 * (`direct`, `organic-google`) and returns the segment. It has to accept both:
 * last touch is always a page key, while first touch is a channel unless the
 * inbound link carried a `utm_source`, in which case it is one of ours.
 */
/**
 * AI assistant hosts, as GA4 reports them in `firstUserSource`. Mirrors the
 * AI_HOSTS table in the marketing site's BaseLayout; keep the two in step.
 *
 * GA4 files all of these under the `Referral` channel group, so without this
 * check an assistant naming us would be indistinguishable from any other site
 * linking to us — and it is the opposite end of the intent range.
 */
const AI_SOURCE_MARKERS = [
  'chatgpt',
  'openai',
  'claude.ai',
  'perplexity',
  'gemini.google',
  'copilot',
];

/** GA4 channel groups that all mean "we paid for this click". */
const GA4_PAID_CHANNEL_PREFIXES = ['Paid', 'Display', 'Cross-network'];

/**
 * Maps GA4's `firstUserDefaultChannelGroup` (plus `firstUserSource`, needed
 * only to pull AI assistants out of Referral) onto a segment.
 *
 * The segment is stored on the row rather than derived at read time, so a
 * change here needs a `mode: 'reingest'` run to take effect on existing dates.
 * That path already exists for exactly this reason.
 */
export function ga4ChannelGroupToSegment(
  channelGroup: string,
  source: string,
): IntentSegment {
  const group = channelGroup ?? '';
  const src = (source ?? '').toLowerCase();

  // Paid is settled before the AI markers are consulted, and the order is the
  // whole point. A bought click carries our own campaign key as the source, so
  // `chatgpt-ad1` matches the `chatgpt` marker and every ChatGPT ad click was
  // being counted as an AI referral — in the HIGH intent band, against a
  // numerator that put the same clicks somewhere else. Measured 2026-09-20:
  // all five paid channel groups returned 'ai-referral' for that source.
  //
  // Our own paid prefix is checked as well as GA4's grouping, and not instead
  // of it. GA4 decides Paid from `utm_medium` and the click id, so an ad URL
  // carrying only `utm_source` lands in Referral or Unassigned; without this
  // line such a click reads as paid on the signup side and as something else
  // on the visitor side, which is the exact mismatch this build exists to end.
  // This makes the two sides agree by construction rather than by convention.
  if (
    GA4_PAID_CHANNEL_PREFIXES.some((p) => group.startsWith(p)) ||
    PAID_UTM_PREFIXES.some((p) => src.startsWith(p))
  ) {
    return 'paid';
  }

  // A campaign key we minted is classified by us, never by GA4's guess at it.
  // GA4 derives its grouping from utm_medium and the click id, so a URL tagged
  // with only utm_source lands in 'Unassigned' — measured 2026-09-20 over the
  // year to that date, 457 users sat there, including `email-relaunch-2026-04-13`
  // and `email-third-wave-2026-04-27`, which the signup side reads as email.
  // Same failure as the paid one above: one key, two segments, both sides wrong.
  if (EMAIL_UTM_PREFIXES.some((p) => src.startsWith(p))) return 'email';
  if (SOCIAL_UTM_PREFIXES.some((p) => src.startsWith(p))) return 'social';

  if (AI_SOURCE_MARKERS.some((m) => src.includes(m))) return 'ai-referral';
  // GA4's own grouping is the safety net behind AI_SOURCE_MARKERS, which is a
  // hand-maintained host list. Every assistant in the group today is matched by
  // that list, so this changes nothing now; it is here so the next assistant
  // Google recognises does not land in 'other' and read as low intent.
  if (group === 'AI Assistant') return 'ai-referral';

  if (group === 'Direct') return 'direct';
  if (group === 'Organic Search') return 'organic-search';
  if (group === 'Organic Social') return 'social';
  if (group === 'Email') return 'email';
  if (group === 'Referral') return 'referral';

  // 'Unassigned', and any group GA4 adds after this was written.
  return 'other';
}

export function classifyIntentSegment(key: string): IntentSegment {
  if (INTENT_SEGMENT_OVERRIDES[key]) return INTENT_SEGMENT_OVERRIDES[key];

  // Exact page keys are matched ahead of every channel prefix because
  // `ai-task-capture` is one of our product pages and would otherwise read as
  // an AI referral. Nothing in PRODUCT_PAGE_KEYS collides with the campaign
  // prefixes below, so moving it up changes no other answer.
  if (PRODUCT_PAGE_KEYS.has(key)) return 'product';

  if (PAID_UTM_PREFIXES.some((p) => key.startsWith(p))) return 'paid';
  if (SOCIAL_UTM_PREFIXES.some((p) => key.startsWith(p))) return 'social';
  if (EMAIL_UTM_PREFIXES.some((p) => key.startsWith(p))) return 'email';

  if (key === DIRECT_UTM_KEY) return 'direct';
  if (ORGANIC_UTM_PREFIXES.some((p) => key.startsWith(p)))
    return 'organic-search';
  if (AI_UTM_PREFIXES.some((p) => key.startsWith(p))) return 'ai-referral';
  if (REFERRAL_UTM_PREFIXES.some((p) => key.startsWith(p))) return 'referral';

  if (key.startsWith(BLOG_POST_UTM_PREFIX)) {
    const slug = key.slice(BLOG_POST_UTM_PREFIX.length);
    if (INFORMATIONAL_BLOG_MARKERS.some((m) => slug.includes(m)))
      return 'informational';
    if (COMPARISON_BLOG_MARKERS.some((m) => slug.includes(m)))
      return 'comparison';
    return 'editorial';
  }
  if (PRODUCT_PAGE_PREFIXES.some((p) => key.startsWith(p))) return 'product';

  // Genuinely unrecognized. Worth looking at rather than filing under a
  // channel it does not belong to — that is what `direct-other` used to do.
  return 'other';
}
